import type {
  ApiKeyModelOption,
  ApiKeyStatus,
  GatewayApiKey,
} from '@/services/business/apiKeys';
import { accountStore } from '../store/account';

export class ApiKeyError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}
type StoredKey = Omit<GatewayApiKey, 'status'>;
export const apiKeyModels: ApiKeyModelOption[] = [
  {
    id: 'model-codex-code',
    displayName: 'Codex Code',
    provider: 'codex',
    available: true,
  },
  {
    id: 'model-grok-chat',
    displayName: 'Grok Chat',
    provider: 'grok',
    available: true,
  },
  {
    id: 'model-gemini-chat',
    displayName: 'Gemini Chat',
    provider: 'gemini',
    available: true,
  },
  {
    id: 'model-claude-code',
    displayName: 'Claude Code',
    provider: 'claude',
    available: true,
  },
  {
    id: 'model-claude-chat',
    displayName: 'Claude Chat',
    provider: 'claude',
    available: false,
  },
];
export function isModelAllowed(
  key: Pick<
    GatewayApiKey,
    'enabled' | 'revokedAt' | 'expiresAt' | 'modelScope' | 'allowedModels'
  >,
  modelId: string,
  now: number,
) {
  if (
    !key.enabled ||
    key.revokedAt ||
    (key.expiresAt && now >= Date.parse(key.expiresAt))
  )
    return false;
  return (
    apiKeyModels.some((model) => model.id === modelId && model.available) &&
    (key.modelScope === 'all' || key.allowedModels.includes(modelId))
  );
}
const keys: StoredKey[] = [];
let sequence = 0;
export function resetApiKeys() {
  const now = Date.now();
  keys.splice(
    0,
    keys.length,
    ...Array.from(
      { length: 6 },
      (_, index): StoredKey => ({
        id: `gateway-key-${index + 1}`,
        name: [
          '业务应用',
          '即将到期应用',
          '内部服务',
          '已停用应用',
          '已过期应用',
          '已撤销应用',
        ][index],
        prefix: `gwk-example-••••${String(index + 1).padStart(4, '0')}`,
        modelScope: index === 0 || index === 1 ? 'allowlist' : 'all',
        allowedModels:
          index === 0
            ? ['model-codex-code']
            : index === 1
              ? ['model-grok-chat', 'model-gemini-chat']
              : [],
        enabled: index !== 3,
        expiresAt:
          index === 2
            ? null
            : new Date(
                now + (index === 4 ? -1 : index === 1 ? 2 : 30) * 86400_000,
              ).toISOString(),
        createdAt: new Date(now - (index + 1) * 86400_000).toISOString(),
        createdBy: 'Admin',
        lastUsedAt: index < 3 ? new Date(now - 3600_000).toISOString() : null,
        disabledAt: index === 3 ? new Date(now - 3600_000).toISOString() : null,
        revokedAt: index === 5 ? new Date(now - 3600_000).toISOString() : null,
        version: 1,
      }),
    ),
  );
  sequence = 6;
}
resetApiKeys();
function statusOf(key: StoredKey): ApiKeyStatus {
  return key.revokedAt
    ? 'revoked'
    : key.expiresAt && Date.now() >= Date.parse(key.expiresAt)
      ? 'expired'
      : key.enabled
        ? 'active'
        : 'disabled';
}
function snapshot(key: StoredKey): GatewayApiKey {
  return { ...key, status: statusOf(key) };
}
function find(id: string) {
  const key = keys.find((item) => item.id === id);
  if (!key) throw new ApiKeyError(404, 'API Key 不存在');
  return key;
}
export function readApiKey(id: string) {
  return snapshot(find(id));
}
const states: ApiKeyStatus[] = ['active', 'disabled', 'expired', 'revoked'];
export function queryApiKeys(input: Record<string, unknown>) {
  const page = Number(input.page ?? 1),
    pageSize = Number(input.page_size ?? 10);
  const { sort, order, status } = input;
  if (
    !Number.isInteger(page) ||
    page < 1 ||
    !Number.isInteger(pageSize) ||
    pageSize < 1 ||
    pageSize > 100 ||
    (status !== undefined && !states.includes(status as ApiKeyStatus)) ||
    (sort !== undefined && !['name', 'status', 'expiresAt'].includes(String(sort))) ||
    (order !== undefined && !['asc', 'desc'].includes(String(order))) ||
    !!sort !== !!order
  )
    throw new ApiKeyError(422, '查询参数无效');
  const q = String(input.q ?? '')
    .trim()
    .toLowerCase();
  const items = keys
    .map(snapshot)
    .filter(
      (key) =>
        (!status || key.status === status) &&
        (!q || `${key.name} ${key.prefix}`.toLowerCase().includes(q)),
    )
    .sort((a, b) => {
      if (!sort)
        return (
          states.indexOf(a.status) - states.indexOf(b.status) ||
          b.createdAt.localeCompare(a.createdAt) ||
          a.id.localeCompare(b.id)
        );
      const av =
        sort === 'status' ? states.indexOf(a.status) : a[sort as 'name' | 'expiresAt'];
      const bv =
        sort === 'status' ? states.indexOf(b.status) : b[sort as 'name' | 'expiresAt'];
      if (av === null || bv === null)
        return av === bv ? a.id.localeCompare(b.id) : av === null ? 1 : -1;
      const diff =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      return diff * (order === 'desc' ? -1 : 1) || a.id.localeCompare(b.id);
    });
  return {
    items: items.slice((page - 1) * pageSize, page * pageSize),
    total: items.length,
    page,
    page_size: pageSize,
  };
}
export function issueApiKey(input: Record<string, unknown>) {
  if (
    typeof input.name !== 'string' ||
    !input.name.trim() ||
    input.name.trim().length > 40 ||
    (input.expiresAt !== null &&
      (typeof input.expiresAt !== 'string' ||
        !Number.isFinite(Date.parse(input.expiresAt)) ||
        Date.parse(input.expiresAt) <= Date.now()))
  )
    throw new ApiKeyError(422, '请填写1–40字的名称和有效的到期时间');
  const name = input.name.trim();
  const modelScope = input.modelScope ?? 'all';
  if (modelScope !== 'all' && modelScope !== 'allowlist')
    throw new ApiKeyError(422, '模型范围无效');
  let allowedModels: string[] = [];
  if (modelScope === 'allowlist') {
    if (!Array.isArray(input.allowedModels) || input.allowedModels.length === 0)
      throw new ApiKeyError(422, '请至少选择一个模型');
    if (new Set(input.allowedModels).size !== input.allowedModels.length)
      throw new ApiKeyError(422, '模型不能重复');
    for (const id of input.allowedModels) {
      const model = apiKeyModels.find((item) => item.id === id);
      if (!model) throw new ApiKeyError(422, '包含未知模型');
      if (!model.available) throw new ApiKeyError(422, '包含不可用模型');
    }
    allowedModels = input.allowedModels.slice();
  }
  if (
    keys.some((key) => !key.revokedAt && key.name.toLowerCase() === name.toLowerCase())
  )
    throw new ApiKeyError(409, 'Key 名称已存在');
  const secret = `gwk-example-${crypto.randomUUID()}`;
  const key: StoredKey = {
    id: `gateway-key-${++sequence}`,
    name,
    modelScope,
    allowedModels,
    prefix: `gwk-example-••••${secret.slice(-4)}`,
    enabled: true,
    expiresAt: input.expiresAt as string | null,
    createdAt: new Date().toISOString(),
    createdBy: accountStore.profile().displayName,
    lastUsedAt: null,
    disabledAt: null,
    revokedAt: null,
    version: 1,
  };
  keys.unshift(key);
  return { key: snapshot(key), secret };
}
export function mutateApiKey(
  id: string,
  input: Record<string, unknown>,
  revoke: boolean,
) {
  if ('modelScope' in input || 'allowedModels' in input)
    throw new ApiKeyError(422, '创建后不可修改模型范围');
  const key = find(id);
  if (
    !Number.isInteger(input.expectedVersion) ||
    Number(input.expectedVersion) < 1 ||
    (!revoke && typeof input.enabled !== 'boolean')
  )
    throw new ApiKeyError(422, 'Key 状态参数无效');
  if (input.expectedVersion !== key.version)
    throw new ApiKeyError(409, 'Key 已更新，请刷新后重试');
  if (key.revokedAt || (!revoke && statusOf(key) === 'expired'))
    throw new ApiKeyError(409, '已过期或撤销的 Key 不可启停');
  if (revoke) key.revokedAt = new Date().toISOString();
  else {
    key.enabled = input.enabled as boolean;
    key.disabledAt = key.enabled ? null : new Date().toISOString();
  }
  key.version += 1;
  return snapshot(key);
}
